import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Typography from '../Typography';
import AttendeeItem from '../EventSummary/AttendeeItem';
import Button from '../Button/Button';
import Drawer from './Drawer';
import { theme } from '../../theme';
import CloseIcon from '../Icons/CloseIcon';
import HelmetIcon from '../Icons/HelmetIcon';

const AllAttendeesDrawer = ({
  isOpen,
  onClose,
  attendees,
  organizer,
  organizerVehicle,
  attendeesCount,
  anchor = 'right',
  width
}) => {
  const { t: tEventDetail } = useTranslation('eventDetail');
  
  // Filtrar asistentes para mostrar solo los que tienen estado 'attending'
  const filteredAttendees = attendees.filter(attendee => attendee.status === 'attending');

  return (
    <Drawer anchor={anchor} open={isOpen} onClose={onClose} width={width}>
      <DrawerHeader>
        <Typography as="h2" $variant="title-5-semibold">
          {tEventDetail('eventAttendees.title')} ({filteredAttendees.length})
        </Typography>
        <Button $iconOnly={true} onClick={onClose} $variant="ghost">
          <CloseIcon fill={theme.colors.defaultMain} width={20} height={20} />
        </Button>
      </DrawerHeader>

      <DrawerBody>
        <AttendeesList>
          {/* Organizador */}
          {organizer._id ? (
            <OrganizerLink to={`/user/${organizer._id}`}>
              <Organizer>
                <OrganizerImages>
                  {organizer.userAvatar ? (
                    <OrganizerAvatar
                      src={organizer.userAvatar}
                      alt={tEventDetail('eventDetailHeader.organizer')}
                    />
                  ) : (
                    <AvatarPlaceholder>
                      <HelmetIcon width={32} height={32} fill={theme.colors.defaultStrong} />
                    </AvatarPlaceholder>
                  )}
                  {organizerVehicle && organizerVehicle.image && (
                    <OrganizerVehicle
                      src={organizerVehicle.image}
                      alt={tEventDetail('eventDetailHeader.organizerVehicle')}
                    />
                  )}
                </OrganizerImages>
                <OrganizerInfo>
                  <Typography
                    $variant="body-3-semibold"
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      width: '100%',
                      textAlign: 'center'
                    }}>
                    {organizer.name} {organizer.lastName}
                  </Typography>
                  {organizerVehicle && organizerVehicle.image && (
                    <Typography
                      $variant="caption-medium"
                      color={theme.colors.defaultWeak}
                      style={{
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        width: '100%',
                        textAlign: 'center'
                      }}>
                      {organizerVehicle.nickname || `${organizerVehicle.brand} ${organizerVehicle.model}`}
                    </Typography>
                  )}
                </OrganizerInfo>
                <OrganizerBadge>
                  <OrganizerBadgeIcon src="/icons/organizer-helmet.svg" alt="Organizer badge" />
                  <Typography $variant="caption-medium" color={theme.colors.brandMain}>
                    {tEventDetail('eventDetailHeader.organizer')}
                  </Typography>
                </OrganizerBadge>
              </Organizer>
            </OrganizerLink>
          ) : (
            <Organizer>
              <OrganizerImages>
                <AvatarPlaceholder>
                  <HelmetIcon width={32} height={32} fill={theme.colors.defaultStrong} />
                </AvatarPlaceholder>
              </OrganizerImages>
              <OrganizerInfo>
                <Typography
                  $variant="body-3-semibold"
                  style={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    width: '100%',
                    textAlign: 'center'
                  }}>
                  Organizador no disponible
                </Typography>
              </OrganizerInfo>
              <OrganizerBadge>
                <OrganizerBadgeIcon src="/icons/organizer-helmet.svg" alt="Organizer badge" />
                <Typography $variant="caption-medium" color={theme.colors.brandMain}>
                  {tEventDetail('eventDetailHeader.organizer')}
                </Typography>
              </OrganizerBadge>
            </Organizer>
          )}

          {/* Asistentes */}
          {filteredAttendees.map((attendee) => (
            <AttendeeItem key={attendee._id} attendee={attendee} />
          ))}
        </AttendeesList>
      </DrawerBody>
    </Drawer>
  );
};

AllAttendeesDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attendees: PropTypes.array.isRequired,
  organizer: PropTypes.object.isRequired,
  organizerVehicle: PropTypes.object,
  attendeesCount: PropTypes.number.isRequired,
  anchor: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default AllAttendeesDrawer;

// Styled Components
const DrawerHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.sizing.sm};
`;

const AttendeesList = styled.div`
  align-items: flex-start;
  display: grid;
  gap: ${({ theme }) => theme.sizing.xs};
  grid-template-columns: repeat(3, minmax(0, 1fr));
  list-style: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const OrganizerLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const Organizer = styled.div`
  align-items: center;
  align-self: flex-start;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  padding: ${({ theme }) => theme.sizing.xs} 0 0 0;
`;

const OrganizerImages = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  padding: 0 ${({ theme }) => theme.sizing.xs};
`;

const OrganizerInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.xs};
  width: 100%;
`;

const OrganizerBadge = styled.div`
  align-items: center;
  align-self: stretch;
  background: ${({ theme }) => theme.fill.brandAlphaMain16};
  display: flex;
  gap: ${({ theme }) => theme.sizing.xxs};
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.xxs};

  .OrganizerBadgeIcon {
    height: ${({ theme }) => theme.sizing.sm};
  }
`;

const OrganizerBadgeIcon = styled.img`
  height: ${({ theme }) => theme.sizing.sm};
`;

const OrganizerAvatar = styled.img`
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  border-radius: ${({ theme }) => theme.radius.xs};
  height: ${({ theme }) => theme.sizing.xl};
  width: ${({ theme }) => theme.sizing.xl};
  z-index: 2;
`;

const OrganizerVehicle = styled.img`
  border-radius: ${({ theme }) => theme.radius.xl};
  height: ${({ theme }) => theme.sizing.lg};
  margin-left: -${({ theme }) => theme.sizing.xxs};
  width: ${({ theme }) => theme.sizing.lg};
  z-index: 1;
`;

const AvatarPlaceholder = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  height: ${({ theme }) => theme.sizing.xl};
  justify-content: center;
  width: ${({ theme }) => theme.sizing.xl};
  z-index: 2;
`;